import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSEO } from './GlobalSEOProvider';
import { generateOptimizedTitle, generateOptimizedDescription, generateOptimizedKeywords } from '@/utils/seoOptimizations';

/**
 * Composant qui optimise automatiquement le SEO selon la route actuelle
 */
const AutoSEOOptimizer: React.FC = () => {
  const location = useLocation();
  const { updatePageSEO, config } = useSEO();

  useEffect(() => {
    const optimizeSEOForCurrentPage = () => {
      const path = location.pathname;
      let seoData = {
        title: '',
        description: '',
        keywords: '',
        canonical: `${config.baseUrl}${path}`,
        ogImage: config.defaultImage
      };

      // Page d'accueil
      if (path === '/') {
        seoData = {
          title: 'Groupe Fabre Environnement - Recyclage et Valorisation des Déchets en Occitanie, Auvergne et Île-de-France | Expert depuis 25+ ans',
          description: '✓ Expert recyclage Occitanie, Auvergne & Île-de-France ✓ Location bennes ✓ Achat métaux ✓ Démolition ✓ Valorisation déchets ✓ 25+ ans expertise ✓ Devis gratuit',
          keywords: generateOptimizedKeywords('valorisation', 'Occitanie, Auvergne, Île-de-France'),
          canonical: config.baseUrl,
          ogImage: config.defaultImage
        };
      }
      
      // Services de location de bennes
      else if (path.includes('/nos-services/location-bennes')) {
        const location = extractLocationFromPath(path);
        seoData = {
          title: generateOptimizedTitle('location-bennes', location || 'Occitanie, Auvergne, Île-de-France'),
          description: generateOptimizedDescription('location-bennes', location || 'Occitanie, Auvergne, Île-de-France'),
          keywords: generateOptimizedKeywords('location-bennes', location || 'Occitanie, Auvergne, Île-de-France'),
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Services d'achat de métaux
      else if (path.includes('/nos-services/achat-metaux')) {
        const location = extractLocationFromPath(path);
        const metalType = extractMetalTypeFromPath(path);
        seoData = {
          title: generateOptimizedTitle('achat-metaux', location || 'Occitanie, Auvergne, Île-de-France', metalType),
          description: generateOptimizedDescription('achat-metaux', location || 'Occitanie, Auvergne, Île-de-France', metalType),
          keywords: generateOptimizedKeywords('achat-metaux', location || 'Occitanie, Auvergne, Île-de-France', metalType),
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Services de démolition
      else if (path.includes('/nos-services/demolition')) {
        const location = extractLocationFromPath(path);
        seoData = {
          title: generateOptimizedTitle('demolition', location || 'Occitanie, Auvergne, Île-de-France'),
          description: generateOptimizedDescription('demolition', location || 'Occitanie, Auvergne, Île-de-France'),
          keywords: generateOptimizedKeywords('demolition', location || 'Occitanie, Auvergne, Île-de-France'),
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Pages de valorisation
      else if (path.includes('/valorisation/')) {
        const wasteType = path.split('/valorisation/')[1]?.split('/')[0];
        seoData = {
          title: `Valorisation ${wasteType?.replace('-', ' ') || 'des déchets'} | Groupe Fabre Environnement`,
          description: `Solutions de valorisation et recyclage ${wasteType?.replace('-', ' ') || 'des déchets'}. Expertise environnementale depuis 25+ ans en Occitanie, Auvergne et Île-de-France.`,
          keywords: generateOptimizedKeywords('valorisation', undefined, wasteType),
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Pages métiers
      else if (path.includes('/nos-metiers/')) {
        const metier = path.split('/nos-metiers/')[1]?.split('/')[0];
        seoData = {
          title: `Gestion déchets ${metier} | Solutions professionnelles | Groupe Fabre`,
          description: `Solutions spécialisées de gestion des déchets pour les ${metier}s. Location bennes, recyclage métaux, valorisation. Service adapté aux professionnels.`,
          keywords: `déchets ${metier}, recyclage professionnel, location bennes ${metier}, Groupe Fabre`,
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Pages contact et institutionnelles
      else if (path === '/contact') {
        seoData = {
          title: 'Contact - Groupe Fabre Environnement | Devis gratuit en 24h',
          description: 'Contactez Groupe Fabre Environnement pour vos besoins en gestion des déchets. Devis gratuit sous 24h. Présents en Occitanie, Auvergne et Île-de-France.',
          keywords: 'contact, devis recyclage, Groupe Fabre contact, gestion déchets devis',
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      else if (path === '/sites') {
        seoData = {
          title: 'Nos Sites de Recyclage en Occitanie, Auvergne et Île-de-France | Groupe Fabre',
          description: 'Découvrez nos sites de recyclage et valorisation des déchets répartis sur plusieurs départements en Occitanie, Auvergne et Île-de-France. Proximité et expertise depuis 25+ ans.',
          keywords: 'sites recyclage Occitanie, Auvergne, Île-de-France, centres valorisation déchets, Groupe Fabre implantations',
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      else if (path === '/histoire') {
        seoData = {
          title: 'Notre Histoire | 25+ ans d\'expertise environnementale | Groupe Fabre',
          description: 'Découvrez l\'histoire du Groupe Fabre Environnement depuis 1999. 25+ ans d\'innovation dans le recyclage et la valorisation des déchets.',
          keywords: 'histoire Groupe Fabre, expertise recyclage, innovation environnement',
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      // Pages utilitaires
      else if (path === '/valoguide') {
        seoData = {
          title: 'ValoGuide - Guide de Valorisation des Déchets | Groupe Fabre Environnement',
          description: 'Guide complet de valorisation des déchets avec conseils pratiques, galerie photos et innovations du secteur environnemental.',
          keywords: 'guide valorisation déchets, conseils recyclage, innovations environnement',
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }
      
      else if (path === '/estimation-metaux') {
        seoData = {
          title: 'Estimation Gratuite de vos Métaux | Meilleurs Prix | Groupe Fabre',
          description: 'Obtenez une estimation gratuite pour vos métaux et ferrailles. Groupe Fabre vous propose les meilleurs prix du marché. Réponse rapide.',
          keywords: 'estimation métaux gratuite, prix ferraille, rachat métaux',
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }

      // Fallback pour les autres pages
      else {
        const pageName = path.split('/').filter(Boolean).join(' ').replace('-', ' ');
        seoData = {
          title: `${pageName.charAt(0).toUpperCase() + pageName.slice(1)} | Groupe Fabre Environnement`,
          description: `Découvrez nos services de ${pageName}. Groupe Fabre Environnement, expert en gestion des déchets depuis 25+ ans.`,
          keywords: `${pageName}, Groupe Fabre, recyclage, valorisation déchets`,
          canonical: `${config.baseUrl}${path}`,
          ogImage: config.defaultImage
        };
      }

      const faq = buildFAQSchema(path);
      if (faq) {
        (seoData as any).structuredData = faq;
      }

      updatePageSEO(seoData);
    };

    optimizeSEOForCurrentPage();
  }, [location.pathname, updatePageSEO]); // Retirer config des dépendances car il ne change jamais

  return null; // Ce composant ne rend rien visuellement
};

// Fonctions utilitaires pour extraire des informations du path
const extractLocationFromPath = (path: string): string | undefined => {
  const locationSegments = [
    'occitanie', 'auvergne', 'ile-de-france', 'idf',
    'aveyron', 'haute-garonne', 'tarn', 'lot', 'cantal', 'lozere', 'herault', 'tarn-et-garonne', 'val-d-oise', 'puy-de-dome'
  ];
  return locationSegments.find(location => path.includes(location));
};

const extractMetalTypeFromPath = (path: string): string | undefined => {
  const metalTypes = ['ferraille', 'cuivre', 'aluminium', 'plomb', 'zinc', 'laiton', 'inox', 'batteries'];
  return metalTypes.find(metal => path.includes(metal));
};

// Génère un schéma FAQPage JSON-LD selon la route
export const buildFAQSchema = (path: string): Record<string, any> | undefined => {
  const qa = (q: string, a: string) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a }
  });

  if (path === '/') {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        qa('Quels services propose Groupe Fabre Environnement ?', 'Location de bennes, achat de métaux, démolition, collecte et valorisation des déchets en Occitanie, Auvergne et Île-de-France.'),
        qa('Dans quels départements intervenez-vous ?', 'Nous intervenons en Occitanie, Auvergne et Île-de-France avec des sites de proximité.'),
        qa('Proposez-vous des devis gratuits ?', 'Oui, un devis gratuit est fourni sous 24h selon votre besoin.')
      ]
    };
  }

  if (path.includes('/nos-services/location-bennes')) {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        qa('Quels types de bennes puis-je louer ?', 'Plusieurs volumes disponibles (petites, moyennes, grandes) selon le type de déchets.'),
        qa('Sous combien de temps livrez-vous la benne ?', 'Livraison rapide sous 24 à 48h selon la zone.'),
        qa('Quels déchets sont acceptés ?', 'Gravats, encombrants, déchets verts, bois, cartons, métaux (hors déchets dangereux).')
      ]
    };
  }

  if (path.includes('/nos-services/achat-metaux')) {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        qa('Quels métaux rachetez-vous ?', 'Ferraille, cuivre, aluminium, inox, zinc, laiton, batteries, et autres métaux valorisables.'),
        qa('Comment calculez-vous le prix ?', 'Selon les cours du marché, la qualité et la quantité, avec pesée contrôlée.'),
        qa('Quels documents sont nécessaires ?', 'Une pièce d\'identité peut être requise selon la réglementation en vigueur.')
      ]
    };
  }

  if (path.includes('/nos-services/demolition')) {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        qa('Prenez-vous en charge la gestion des déchets de chantier ?', 'Oui, tri, collecte et valorisation sont assurés conformément à la réglementation.'),
        qa('Intervenez-vous pour des bâtiments industriels ?', 'Oui, nous gérons des chantiers de toutes tailles avec sécurité et traçabilité.')
      ]
    };
  }

  if (path.includes('/valorisation/')) {
    const type = path.split('/valorisation/')[1]?.split('/')[0]?.replace('-', ' ') || 'déchets';
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        qa(`Comment valorisez-vous les ${type} ?`, 'Par tri, préparation matière et filières de recyclage adaptées, pour maximiser la réutilisation.'),
        qa('Proposez-vous un accompagnement réglementaire ?', 'Oui, nous fournissons conseils, pesées et preuves de traçabilité.')
      ]
    };
  }

  // Fallback générique
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      qa('Comment obtenir un devis ?', 'Contactez-nous via le formulaire ou par téléphone. Réponse rapide sous 24h.'),
      qa('Que devient mon déchet après collecte ?', 'Il est trié, orienté vers les filières de valorisation et recyclé lorsque c\'est possible.')
    ]
  };
};

export default AutoSEOOptimizer;